import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";
import {
  updateAdmins,
  adminsRecord,
  clearState,
} from "../../redux/Slices/AdminsSlice";
import { getRoles } from "../../redux/Slices/authSlice";

const EditAdmin = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    password: "",
    email: "",
    image: null,
    role: "",
    has_wallet: "1",
  });

  const [originalData, setOriginalData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const { isLoading: loading, error, success, record } = useSelector(
    (state) => state.admins
  );
  const { roles } = useSelector((state) => state.auth);

  useEffect(() => {
    dispatch(getRoles());
    dispatch(adminsRecord(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (record) {
      const original = {
        name: record?.name || "",
        email: record?.email || "",
        password: "",
        role: record?.role_id || "",
        image: record?.image || null,
        has_wallet: (record?.has_wallet === 1 || record?.has_wallet === "1" || record?.has_wallet === true) ? "1" : "0",
      };
      setOriginalData(original);
      setFormData(original);
    }
  }, [record]);

  // Check for form differences
  useEffect(() => {
    if (originalData) {
      const hasFormChanges =
        formData.name !== originalData.name ||
        formData.email !== originalData.email ||
        formData.role !== originalData.role ||
        formData.has_wallet !== originalData.has_wallet ||
        formData.password !== "" ||
        (formData.image !== null && formData.image instanceof File);

      setHasChanges(hasFormChanges);
    }
  }, [formData, originalData]);

  useEffect(() => {
    if (success) {
      toast.success("تم التحديث بنجاح", { position: "top-center", zIndex: 9999 });
      dispatch(clearState());
      navigate("/admins");
    }
    if (error && error !== null) {
      let errorMessage = "";
      if (typeof error === "object" && error.errors) {
        const firstKey = Object.keys(error.errors)[0];
        const firstError = error.errors[firstKey][0];
        if (firstKey === "email" && firstError.includes("has already been taken")) {
          errorMessage = "هذا البريد الإلكتروني مستخدم بالفعل";
        } else {
          errorMessage = firstError;
        }
      } else if (typeof error === "string") {
        if (error.includes("email") && error.includes("taken")) {
          errorMessage = "هذا البريد الإلكتروني مستخدم بالفعل";
        } else {
          errorMessage = error;
        }
      } else {
        errorMessage = "حدث خطأ غير متوقع";
      }

      toast.error(errorMessage, { position: "top-center", zIndex: 9999 });
      dispatch(clearState());
    }
  }, [success, error, dispatch, navigate]);

  useEffect(() => {
    return () => {
      dispatch(clearState());
    };
  }, [dispatch]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: null,
      }));
      return;
    }

    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      toast.error("الملف يجب أن يكون صورة (jpg, jpeg, png)");
      e.target.value = "";
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: null,
      }));
      return;
    }

    if (file instanceof File) {
      setFormData((prev) => ({
        ...prev,
        [e.target.name]: file,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(clearState());

    if (!formData.name.trim()) {
      toast.error("يرجى إدخال اسم الأدمن", { position: "top-center", zIndex: 9999 });
      return;
    }

    const data = new FormData();
    data.append("name", formData.name);
    data.append("email", formData.email);
    data.append("role", formData.role);
    data.append("has_wallet", formData.has_wallet);

    if (formData.password && formData.password.trim()) {
      data.append("password", formData.password);
    }
    if (formData.image && formData.image instanceof File) {
      data.append("image", formData.image);
    }

    dispatch(updateAdmins({ id, data }));
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 9,
    border: "1.5px solid #e2e8f0",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    background: "#fff",
    transition: "border-color 0.2s, box-shadow 0.2s"
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    display: "block",
    marginBottom: 6,
    textAlign: "right"
  };

  const focusHandler = (e) => {
    e.target.style.borderColor = "var(--main-color)";
    e.target.style.boxShadow = "0 0 0 3px rgba(228, 109, 23, 0.1)";
  };

  const blurHandler = (e) => {
    e.target.style.borderColor = "#e2e8f0";
    e.target.style.boxShadow = "none";
  };

  return (
    <div style={{ padding: "4px 0", direction: "rtl" }}>
      <style>{`
        .admin-custom-select {
          width: 100% !important;
          padding: 10px 14px !important;
          padding-left: 40px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>") !important;
          background-repeat: no-repeat !important;
          background-position: left 14px center !important;
          background-size: 16px !important;
          cursor: pointer !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .admin-custom-select:focus {
          border-color: var(--main-color) !important;
          box-shadow: 0 0 0 3px rgba(228, 109, 23, 0.1) !important;
        }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            🛡 تعديل حساب المشرف
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>تعديل تفاصيل وحالة المشرف {record?.name ? `(${record.name})` : ""}</p>
        </div>
        <Link to="/admins" className="text-decoration-none">
          <button style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 18px", borderRadius: 10, border: "1px solid #cbd5e1", background: "#fff", color: "#475569", fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
            <FaArrowLeft size={12} /> العودة
          </button>
        </Link>
      </div>

      {/* ── Form Card ─────────────────────────────────── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "24px 30px", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>اسم المشرف *</label>
              <input
                type="text"
                style={inputStyle}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                onFocus={focusHandler}
                onBlur={blurHandler}
                placeholder="أدخل الاسم الكامل للمشرف"
                required
              />
            </div>
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>البريد الإلكتروني *</label>
              <input
                type="email"
                style={inputStyle}
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                onFocus={focusHandler}
                onBlur={blurHandler}
                placeholder="مثال: admin@mystore.education"
                required
              />
            </div>
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>كلمة المرور الجديدة (اختياري)</label>
              <input
                type="password"
                style={inputStyle}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                onFocus={focusHandler}
                onBlur={blurHandler}
                placeholder="أدخل كلمة مرور جديدة لتعديلها أو اتركها فارغة"
              />
            </div>
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>تحديث صورة الحساب</label>
              <input
                type="file"
                style={inputStyle}
                name="image"
                onChange={handleFileChange}
                onFocus={focusHandler}
                onBlur={blurHandler}
              />
              {record?.image && (
                <div className="mt-2">
                  <small className="text-muted">الصورة الحالية:</small>
                  <br />
                  <img 
                    src={record.image} 
                    alt="Current admin avatar" 
                    style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: "1.5px solid #cbd5e1" }}
                    className="mt-1"
                  />
                </div>
              )}
            </div>
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>الدور والصلاحية الممنوحة *</label>
              <select
                className="admin-custom-select"
                name="role"
                value={formData.role ? String(formData.role) : ""}
                onChange={handleInputChange}
                required
              >
                <option value="" disabled>اختر دور</option>
                {roles && roles.length > 0 && roles.map((opt) => (
                  <option key={opt.id} value={String(opt.id)}>{({ "Super Admin": "مدير عام", "Admin": "مشرف", "Editor": "محرر", "Viewer": "مشاهد", "Shipping Company": "شركة شحن" })[opt.name] || opt.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6 mb-3">
              <label style={labelStyle}>تفعيل خزنة/محفظة مالية للموظف؟ *</label>
              <select
                className="admin-custom-select"
                name="has_wallet"
                value={formData.has_wallet ? String(formData.has_wallet) : "1"}
                onChange={handleInputChange}
                required
              >
                <option value="1">نعم (تفعيل الخزنة لاستلام التوريدات المالية)</option>
                <option value="0">لا (بدون خزنة مالية)</option>
              </select>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, borderTop: "1px solid #f1f5f9", marginTop: 20, paddingTop: 20 }}>
            <button type="button" onClick={() => navigate("/admins")} style={{ padding: "9px 22px", borderRadius: 9, border: "1px solid #cbd5e1", background: "#fff", color: "#475569", fontWeight: 700, cursor: "pointer" }}>إلغاء</button>
            <button type="submit" disabled={loading || !hasChanges} style={{ padding: "9px 28px", borderRadius: 9, border: "none", background: hasChanges ? "linear-gradient(135deg,var(--main-color),var(--main-light))" : "#cbd5e1", color: "#fff", fontWeight: 700, cursor: hasChanges ? "pointer" : "not-allowed" }}>
              {loading ? 'جاري الحفظ...' : 'حفظ التعديلات'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAdmin;
